import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, CardActionArea, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Switch, Tooltip, Chip, Pagination, Paper, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { getFlows, updateFlow, deleteFlow } from '../api/whatsapp';
import BuildWithAIModal from './BuildWithAIModal';

const ChatbotFlow = ({ onCreateNew, onEdit }) => {
  const [flows, setFlows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const limit = 10;

  const fetchFlows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFlows({ search, page, limit });
      setFlows(data.flows || []);
      setTotal(data.total || 0);
    } catch {
      setSnack({ open: true, message: 'Failed to load flows', severity: 'error' });
    }
    setLoading(false);
  }, [search, page]);

  useEffect(() => { fetchFlows(); }, [fetchFlows]);

  useEffect(() => { setPage(1); }, [search]);

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await updateFlow(id, { status: newStatus });
      setFlows(prev => prev.map(f => f._id === id ? { ...f, status: newStatus } : f));
    } catch {
      setSnack({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await deleteFlow(deleteDialog);
      setFlows(prev => prev.filter(f => f._id !== deleteDialog));
      setSnack({ open: true, message: 'Flow deleted', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Failed to delete flow', severity: 'error' });
    }
    setDeleteDialog(null);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, color: '#1e293b' }}>Chatbot Flow</Typography>
      <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>Create and manage auto-reply flows for incoming WhatsApp messages</Typography>

      {/* Create Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 200px', minWidth: 200, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
          <CardActionArea onClick={() => setAiModalOpen(true)} sx={{ p: 2, textAlign: 'center' }}>
            <AutoAwesomeIcon sx={{ fontSize: 36, color: '#f59e0b', mb: 1 }} />
            <Typography fontWeight={700} fontSize={14} color="#1e293b">Build with AI</Typography>
            <Typography variant="caption" color="#64748b">Describe your business, AI creates the flow</Typography>
          </CardActionArea>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: 200, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
          <CardActionArea onClick={() => onCreateNew?.()} sx={{ p: 2, textAlign: 'center' }}>
            <SmartToyIcon sx={{ fontSize: 36, color: '#075e54', mb: 1 }} />
            <Typography fontWeight={700} fontSize={14} color="#1e293b">Keyword Chatbot Flow</Typography>
            <Typography variant="caption" color="#64748b">Reply with specific keywords as triggers</Typography>
          </CardActionArea>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: 200, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
          <CardActionArea onClick={() => onCreateNew?.()} sx={{ p: 2, textAlign: 'center' }}>
            <AddIcon sx={{ fontSize: 36, color: '#075e54', mb: 1 }} />
            <Typography fontWeight={700} fontSize={14} color="#1e293b">Start from scratch</Typography>
            <Typography variant="caption" color="#64748b">Create a chatbot flow from scratch</Typography>
          </CardActionArea>
        </Card>
      </Box>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0' }}>
          <TextField
            placeholder="Search flows..."
            size="small" value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
              sx: { borderRadius: 2.5, fontSize: 13, bgcolor: '#f0f2f5', '& fieldset': { border: 'none' } },
            }}
            sx={{ minWidth: 250 }}
          />
          <Typography variant="caption" color="#64748b" sx={{ ml: 'auto' }}>
            Showing {flows.length} of {total} entries
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 12, color: '#64748b', py: 1.5 } }}>
                <TableCell>Name</TableCell>
                <TableCell>Trigger Keywords</TableCell>
                <TableCell>Greeting Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><Typography color="#94a3b8">Loading...</Typography></TableCell></TableRow>
              ) : flows.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="#94a3b8">{search ? 'No flows match your search' : 'No chatbot flows yet. Create one above!'}</Typography>
                </TableCell></TableRow>
              ) : flows.map(flow => (
                <TableRow key={flow._id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& td': { py: 1.2 } }}>
                  <TableCell>
                    <Typography fontWeight={600} fontSize={14} color="#1e293b">{flow.name}</Typography>
                    <Typography variant="caption" color="#94a3b8">
                      Created {new Date(flow.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 250 }}>
                      {(flow.triggerKeywords || []).slice(0, 3).map((kw, i) => (
                        <Chip key={i} label={kw} size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#f0f2f5', color: '#475569', fontWeight: 500 }} />
                      ))}
                      {(flow.triggerKeywords || []).length > 3 && (
                        <Chip label={`+${flow.triggerKeywords.length - 3}`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#e2e8f0', color: '#64748b' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="#475569" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
                      {flow.greetingMessage || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={flow.status === 'active'}
                      onChange={() => handleToggleStatus(flow._id, flow.status)}
                      size="small"
                      sx={{ '& .MuiSwitch-thumb': { bgcolor: flow.status === 'active' ? '#075e54' : '#94a3b8' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit?.(flow._id)} sx={{ color: '#64748b' }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteDialog(flow._id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {total > limit && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / limit)} page={page} onChange={(_, p) => setPage(p)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Delete Flow</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this flow? This action cannot be undone.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <BuildWithAIModal open={aiModalOpen} onClose={() => setAiModalOpen(false)} onCreated={() => { setAiModalOpen(false); fetchFlows(); }} />

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatbotFlow;
